import React, { useMemo, useState } from "react";
import {
  Modal,
  Upload,
  Button,
  Row,
  Col,
  Table,
  message,
  Typography,
  Tabs,
} from "antd";
import type { UploadProps } from "antd";
import * as XLSX from "xlsx";
import styles from "./ModalImport.module.scss";
import { buildColumnsWithHeaderFilters } from "../FilterBar";
import {
  formatImportPreviewValue,
  getImportValueByDataIndex,
  isCurrencyImportColumn,
} from "./helpers";

const { Text } = Typography;

// ===================== types =====================
type BackendError = {
  index: number; // 0-based theo mảng import
  code?: string;
  address?: string;
  reason: string;
};

type PrecheckSheetResult = {
  sheet: string;
  error: string; // "Check thành công"...
  countAdded: number;
  addedErrorList: BackendError[];
};

export type PrecheckResponse = {
  success: boolean;
  data: {
    fileToImport: string; // có thể "" tùy backend
    versionName: string;
    versionId: string;
    result: PrecheckSheetResult[];
  };
};

export type ConfirmPayload = {
  fileName: string; // backend bạn có thể cần, mình để optional
  versionId: string;
  validFrom: string;
};

type ModalImportProps = {
  open?: boolean;
  onCancel?: () => void;

  /** columns table truyền từ màn hình cha (single columns mode) */
  columns?: any[];

  /** ✅ bật chế độ columns theo sheet */
  multipleColumns?: boolean;

  /** ✅ map sheetName -> columns (khi multipleColumns=true) */
  columnsBySheet?: Record<string, any[]>;

  /** optional: API pre-check upload file */
  precheckApi?: (file: File) => Promise<PrecheckResponse>;

  /** optional: API confirm JSON */
  confirmApi?: (payload: ConfirmPayload) => Promise<any>;

  /** optional: nếu confirm cần validFrom */
  validFrom?: string;

  /** optional: custom footer */
  footer?: React.ReactNode;

  /** optional: custom more body */
  children?: React.ReactNode;
};

const { confirm } = Modal;

// ===================== helpers =====================
function sanitizeColumns(columns: any[]): any[] {
  return (columns || [])
    .filter((col) => col && typeof col === "object")
    .map((col) => {
      const next = { ...col };

      if (Array.isArray(next.children)) {
        next.children = sanitizeColumns(next.children);
      }

      return next;
    });
}
const normalize = (v: any) => String(v ?? "").trim();

const toNumberSafe = (v: any) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return v;
  const s = String(v).replace(/[^\d.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

function buildExcelRecord(headers: string[], rowArr: any[]) {
  const record: Record<string, any> = {};
  headers.forEach((h, i) => (record[h] = rowArr?.[i] ?? ""));
  return record;
}

/** ✅ flatten columns để lấy ra leaf columns (support children), giữ nguyên object gốc */
function flattenLeafColumns(columns: any[]) {
  const out: any[] = [];

  const walk = (cols: any[]) => {
    (cols || []).forEach((c: any) => {
      if (!c || typeof c !== "object") return;

      if (Array.isArray(c.children) && c.children.length > 0) {
        walk(c.children);
        return;
      }

      out.push(c);
    });
  };

  walk(columns || []);
  return out;
}

/**
 * Parse XLSX để preview table theo từng sheet.
 * - Hỗ trợ col.importTitle:
 *    + function(row, record) => value
 *    + string => lấy theo record[string]
 * - Nếu không có importTitle, fallback theo title/dataIndex.
 *
 * ✅ Support columns có children (flatten leaf)
 * ✅ Row có: __sheet, __rowIndex, __excelRow, __raw
 * ✅ MULTI columns by sheet: nhận getColsBySheet(sheetName)
 */
async function parseXlsxPreviewBySheet(
  file: File,
  getColsBySheet: (sheetName: string) => any[],
) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });

  const out: Record<string, any[]> = {};

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    if (!ws) {
      out[sheetName] = [];
      continue;
    }

    const rowsArr = XLSX.utils.sheet_to_json<any[]>(ws, {
      header: 1,
      defval: "",
      raw: true,
    });

    if (!rowsArr.length) {
      out[sheetName] = [];
      continue;
    }

    const headers = (rowsArr[0] || []).map(normalize);
    const dataRows = rowsArr.slice(1);

    // ✅ lấy columns theo sheet
    const columns = getColsBySheet(sheetName) || [];
    const leafCols = flattenLeafColumns(columns);
    const mappedCols = (leafCols || []).filter(
      (c: any) => !c.hidden && c.dataIndex,
    );

    const rows: any[] = [];
    dataRows.forEach((rowArr, idx) => {
      const record = buildExcelRecord(headers, rowArr);
      const isEmpty = Object.values(record).every((v) => normalize(v) === "");
      if (isEmpty) return;

      const row: any = {
        id: `${sheetName}_${Date.now()}_${idx}`,
        __sheet: sheetName,
        __rowIndex: idx, // 0-based
        __excelRow: idx + 2, // row thật trong Excel (header là 1)
        __sttExcel: idx + 1, // ✅ STT data row (1,2,3...) đúng vị trí trong Excel
        __raw: record,
      };

      mappedCols.forEach((col: any) => {
        const dataIndex = col.dataIndex;

        // tính value v như cũ
        let finalValue: any;
        if (col.importTitle) {
          const v =
            typeof col.importTitle === "function"
              ? col.importTitle(row, record)
              : (record[col.importTitle] ?? "");
          finalValue = col.editType === "InputNumber" ? toNumberSafe(v) : v;
        } else {
          const title = typeof col.title === "string" ? col.title : "";
          const fieldFlat = String(dataIndex).trim();
          const v = record[title] ?? record[fieldFlat] ?? "";
          finalValue = col.editType === "InputNumber" ? toNumberSafe(v) : v;
        }

        // ✅ set đúng nested structure cho antd
        setByDataIndex(row, dataIndex, finalValue);

        // (khuyến nghị) giữ luôn key phẳng để tương thích logic cũ của bạn
        row[String(dataIndex).trim()] = finalValue;
      });

      rows.push(row);
    });

    out[sheetName] = rows;
  }

  return out;
}

/** build error maps theo sheet */
function buildErrorMapBySheet(pre: PrecheckResponse | null) {
  const maps: Record<string, Map<number, BackendError[]>> = {};
  const result = pre?.data?.result || [];

  result.forEach((r) => {
    const m = new Map<number, BackendError[]>();
    (r.addedErrorList || []).forEach((e) => {
      if (!m.has(e.index)) m.set(e.index, []);
      m.get(e.index)!.push(e);
    });
    maps[r.sheet] = m;
  });

  return maps;
}

function sortRowsErrorFirst(rows: any[], errMap: Map<number, BackendError[]>) {
  return [...(rows || [])].sort((a, b) => {
    const aErr = errMap?.has(a.__rowIndex) ? 1 : 0;
    const bErr = errMap?.has(b.__rowIndex) ? 1 : 0;

    // lỗi lên đầu
    if (aErr !== bErr) return bErr - aErr;

    // giữ nguyên thứ tự theo Excel trong từng nhóm
    return (a.__rowIndex ?? 0) - (b.__rowIndex ?? 0);
  });
}
function setByDataIndex(target: any, dataIndex: any, value: any) {
  if (!target) return;

  // dataIndex dạng mảng: ["cost","depreciation"]
  if (Array.isArray(dataIndex)) {
    let cur = target;
    for (let i = 0; i < dataIndex.length; i++) {
      const key = dataIndex[i];
      if (i === dataIndex.length - 1) {
        cur[key] = value;
      } else {
        cur[key] = cur[key] ?? {};
        cur = cur[key];
      }
    }
    return;
  }

  // dataIndex dạng string có dot: "cost.depreciation"
  if (typeof dataIndex === "string" && dataIndex.includes(".")) {
    const parts = dataIndex.split(".");
    let cur = target;
    for (let i = 0; i < parts.length; i++) {
      const key = parts[i];
      if (i === parts.length - 1) {
        cur[key] = value;
      } else {
        cur[key] = cur[key] ?? {};
        cur = cur[key];
      }
    }
    return;
  }

  // dataIndex bình thường
  target[dataIndex] = value;
}

// ===================== component =====================
const ModalImport = (props: ModalImportProps) => {
  const [filters, setFilters] = useState<any>({});
  const [fileName, setFileName] = useState<string>("Vui lòng chọn file");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // rows preview theo sheet
  const [rowsBySheet, setRowsBySheet] = useState<Record<string, any[]>>({});
  const [activeSheet, setActiveSheet] = useState<string>("");

  const [loading, setLoading] = useState(false);

  const [precheck, setPrecheck] = useState<PrecheckResponse | null>(null);
  const [errorMapBySheet, setErrorMapBySheet] = useState<
    Record<string, Map<number, BackendError[]>>
  >({});

  const sheetResults: PrecheckSheetResult[] = precheck?.data?.result || [];
  const backendSheetNames = sheetResults.map((x) => x.sheet);

  // Nếu backend chưa có result (chưa precheck) => fallback sheet từ workbook
  const sheetNamesToShow =
    backendSheetNames.length > 0 ? backendSheetNames : Object.keys(rowsBySheet);

  const anySheetHasError =
    sheetResults.length > 0
      ? sheetResults.some((r) => (r.addedErrorList || []).length > 0)
      : Object.values(errorMapBySheet).some((m) => (m?.size || 0) > 0);

  /** ✅ lấy columns theo sheet */
  const getColumnsForSheet = (sheet: string) => {
    if (props.multipleColumns) return props.columnsBySheet?.[sheet] || [];
    return props.columns || [];
  };

  /**
   * ✅ Đệ quy để override render cho leaf columns có importTitle (support children)
   * ✅ Kết hợp đúng với parseXlsxPreviewBySheet đang build row theo key phẳng:
   */
  const applyImportRenderRecursive = (cols: any[]): any[] => {
    return (cols || [])
      .filter((col) => col && typeof col === "object")
      .map((col: any) => {
        if (Array.isArray(col.children) && col.children.length > 0) {
          return {
            ...col,
            children: applyImportRenderRecursive(col.children),
          };
        }

        if (!col?.importTitle && !isCurrencyImportColumn(col)) return col;

        const dataIndex = col.dataIndex;

        return {
          ...col,
          render: (value: any, record: any) => {
            const v = getImportValueByDataIndex(record, dataIndex);
            const finalVal = v ?? value;

            return formatImportPreviewValue(finalVal, col);
          },
        };
      });
  };
  /** build columns hoàn chỉnh cho từng sheet (filter + import render + error col) */
  const buildColumnsForSheet = (sheet: string) => {
    const cols = sanitizeColumns(getColumnsForSheet(sheet) || []);

    const base = sanitizeColumns(
      buildColumnsWithHeaderFilters(cols, filters, setFilters) || [],
    );

    const baseWithImportRender = sanitizeColumns(
      applyImportRenderRecursive(base) || [],
    );

    if (!props.precheckApi) return baseWithImportRender;

    const errCol = {
      title: "Lỗi",
      dataIndex: "__errors",
      fixed: "right" as const,
      width: 180,
      render: (_: any, record: any) => {
        const map = errorMapBySheet[sheet] || new Map();
        const errs = map.get(record.__rowIndex);
        if (!errs?.length) return null;

        return (
          <div style={{ color: "#cf1322" }}>
            {errs.slice(0, 2).map((e: any, i: number) => (
              <div key={i}>
                • {e.reason}
                {e.address ? (
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    <i>{e.address}</i>
                  </div>
                ) : null}
              </div>
            ))}
            {errs.length > 2 && <div>... +{errs.length - 2} lỗi</div>}
          </div>
        );
      },
    };

    return sanitizeColumns([...baseWithImportRender, errCol]);
  };

  /** ✅ memo columns per sheet */
  const columnsBySheetRender = useMemo(() => {
    const out: Record<string, any[]> = {};
    for (const sheet of sheetNamesToShow) {
      out[sheet] = buildColumnsForSheet(sheet);
    }
    return out;
  }, [
    sheetNamesToShow,
    props.columns,
    props.columnsBySheet,
    props.multipleColumns,
    filters,
    errorMapBySheet,
    props.precheckApi,
  ]);

  const runPrecheckIfAny = async (file: File) => {
    if (!props.precheckApi) return;

    const res = await props.precheckApi(file);
    setPrecheck(res);

    const maps = buildErrorMapBySheet(res);
    setErrorMapBySheet(maps);

    // active sheet ưu tiên sheet đầu backend trả về
    const firstBackendSheet = res?.data?.result?.[0]?.sheet;
    if (firstBackendSheet) setActiveSheet(firstBackendSheet);
  };

  const uploadProps: UploadProps = {
    maxCount: 1,
    accept: ".xlsx",
    showUploadList: false,
    beforeUpload: async (file) => {
      try {
        setLoading(true);

        // reset state
        setSelectedFile(file);
        setFileName(file.name);
        setRowsBySheet({});
        setPrecheck(null);
        setErrorMapBySheet({});
        setActiveSheet("");

        // 1) parse preview multi-sheet (✅ columns theo sheet)
        const preview = await parseXlsxPreviewBySheet(file, getColumnsForSheet);
        setRowsBySheet(preview);

        const firstSheet = Object.keys(preview)[0] || "";
        if (firstSheet) setActiveSheet(firstSheet);

        // 2) pre-check
        if (props.precheckApi) {
          await runPrecheckIfAny(file);
        }
      } catch (e: any) {
        message.error(e?.message || "Không đọc/Pre-check được file");
        setSelectedFile(null);
        setFileName("Vui lòng chọn file");
        setRowsBySheet({});
        setPrecheck(null);
        setErrorMapBySheet({});
        setActiveSheet("");
      } finally {
        setLoading(false);
      }

      return Upload.LIST_IGNORE;
    },
  };

  const resetAll = () => {
    setFilters({});
    setFileName("Vui lòng chọn file");
    setSelectedFile(null);

    setRowsBySheet({});
    setActiveSheet("");

    setLoading(false);

    setPrecheck(null);
    setErrorMapBySheet({});
  };

  const onConfirm = async () => {
    if (!props.confirmApi) {
      message.warning("Chưa truyền confirmApi");
      return;
    }

    if (!selectedFile) {
      message.error("Chưa chọn file");
      return;
    }

    if (!precheck?.data?.fileToImport) {
      message.error("Chưa pre-check");
      return;
    }

    if (anySheetHasError) {
      message.error("File còn lỗi, không thể xác nhận");
      return;
    }

    try {
      setLoading(true);

      const res = await props.confirmApi({
        fileName: precheck.data.fileToImport,
        versionId: precheck.data.versionId,
        validFrom: props.validFrom ?? "",
      });
      if (res.success) {
        message.success("Xác nhận import thành công");
        resetAll();
        props.onCancel?.();
      }
    } catch (e: any) {
      message.error(e?.message || "Xác nhận import thất bại");
    } finally {
      setLoading(false);
    }
  };

  // ======== UI stats (global) ========
  const totalAll = sheetNamesToShow.reduce(
    (sum, s) => sum + ((rowsBySheet[s] || []).length || 0),
    0,
  );

  // lỗi theo sheet lấy từ errorMapBySheet (ưu tiên backend maps)
  const totalErrorSheets = sheetNamesToShow.reduce((sum, s) => {
    const m = errorMapBySheet[s];
    return sum + (m ? m.size : 0);
  }, 0);

  const canConfirm = !!props.confirmApi && !!selectedFile && !anySheetHasError;

  const rowsBySheetSorted = useMemo(() => {
    const out: Record<string, any[]> = {};
    for (const sheet of Object.keys(rowsBySheet || {})) {
      const errMap =
        errorMapBySheet[sheet] || new Map<number, BackendError[]>();
      out[sheet] = sortRowsErrorFirst(rowsBySheet[sheet] || [], errMap);
    }
    return out;
  }, [rowsBySheet, errorMapBySheet]);

  return (
    <Modal
      open={props.open}
      className={styles.Modal}
      width={"95%"}
      onCancel={() => {
        resetAll();
        props.onCancel?.();
      }}
      title="Import"
      bodyProps={{ className: styles.ModalBody }}
      footer={
        props.footer ?? (
          <div>
            <Button
              style={{ fontWeight: "bold", marginRight: "10px" }}
              onClick={() => {
                confirm({
                  title: "Xác nhận đóng",
                  content: `Bạn chưa tiến hành import, có xác nhận đóng`,
                  okText: "Đóng",
                  cancelText: "Huỷ",
                  okType: "danger",
                  onOk() {
                    props.onCancel?.();
                    resetAll();
                  },
                  onCancel() {},
                });
              }}
            >
              Hủy
            </Button>
            <Button
              style={{
                fontWeight: "bold",
                backgroundColor: "#0076E3",
                color: "white",
              }}
              loading={loading}
              disabled={!canConfirm}
              onClick={onConfirm}
            >
              Xác Nhận
            </Button>
          </div>
        )
      }
    >
      {/* ===== top bar ===== */}
      <div className={styles.BarImport}>
        <Row justify="space-between" align="middle">
          <Col>
            <Row>
              <Col>
                <div>Tên file:</div>
                <div>Tổng số lượng:</div>
                <div>Lỗi:</div>

                {precheck?.data?.versionName ? <div>Version:</div> : null}
                {precheck?.data?.versionId ? <div>VersionId:</div> : null}
              </Col>

              <Col style={{ marginLeft: "20px" }}>
                <div>
                  <Text strong>{fileName}</Text>
                </div>
                <div>{totalAll} dòng</div>
                <div
                  style={{ color: totalErrorSheets ? "#cf1322" : undefined }}
                >
                  {totalErrorSheets} dòng
                </div>

                {precheck?.data?.versionName ? (
                  <div>
                    <Text code>{precheck.data.versionName}</Text>
                  </div>
                ) : null}

                {precheck?.data?.versionId ? (
                  <div>
                    <Text code>{precheck.data.versionId}</Text>
                  </div>
                ) : null}
              </Col>
            </Row>
          </Col>

          <Col>
            <Upload {...uploadProps}>
              <Button
                icon={<img src="/icon/xlsx.svg" />}
                style={{
                  backgroundColor: "#00529F",
                  color: "white",
                  fontWeight: "bold",
                }}
                loading={loading}
              >
                Chọn file
              </Button>
            </Upload>
          </Col>
        </Row>
      </div>

      {/* ===== tabs per sheet ===== */}
      <Tabs
        activeKey={activeSheet || (sheetNamesToShow[0] ?? "")}
        onChange={(k) => setActiveSheet(k)}
        items={sheetNamesToShow.map((sheet) => {
          const sheetRows = rowsBySheetSorted[sheet] || [];
          const errMap =
            errorMapBySheet[sheet] || new Map<number, BackendError[]>();

          const backendInfo = sheetResults.find((x) => x.sheet === sheet);

          const total = sheetRows.length;
          const errorLines = errMap.size;

          return {
            key: sheet,
            label: (
              <span>
                {sheet}{" "}
                {errorLines > 0 ? (
                  <Text type="danger">({errorLines} lỗi)</Text>
                ) : (
                  <Text type="success">(OK)</Text>
                )}
              </span>
            ),
            children: (
              <>
                <div style={{ marginBottom: 8, display: "flex", gap: 16 }}>
                  <div>
                    Tổng: <b>{total}</b>
                  </div>
                  <div>
                    Hợp lệ: <b>{total - errorLines}</b>
                  </div>
                  <div style={{ color: errorLines ? "#cf1322" : undefined }}>
                    Lỗi: <b>{errorLines}</b>
                  </div>
                  {backendInfo?.error ? (
                    <div style={{ marginLeft: "auto" }}>
                      <Text type={errorLines ? "danger" : "success"}>
                        {backendInfo.error}
                      </Text>
                    </div>
                  ) : null}
                </div>

                <Table
                  size="small"
                  dataSource={sheetRows}
                  columns={(columnsBySheetRender[sheet] || []).map(
                    (ite: any, index) => ({
                      ...ite,
                      render:
                        index == 0
                          ? (row, record: any) => record.__rowIndex + 1
                          : ite.render,
                    }),
                  )}
                  rowKey="id"
                  pagination={{ showSizeChanger: true }}
                  scroll={{ x: "max-content" }}
                  rowClassName={(record: any) =>
                    props.precheckApi && errMap.has(record.__rowIndex)
                      ? styles.rowError
                      : ""
                  }
                  style={{ width: "100%" }}
                />
              </>
            ),
          };
        })}
      />

      {props.children}
    </Modal>
  );
};

export { ModalImport };
